import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Stack, Wrench, SealCheck, WarningOctagon, ChartLineUp, ListChecks, Clock, Pulse,
  BellRinging, Sliders, DotsSixVertical, ArrowCounterClockwise, Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const META = {
  total_assets: { icon: Stack, label: "Anlagen gesamt", tone: "text-primary" },
  critical_assets: { icon: WarningOctagon, label: "Kritische Anlagen", tone: "text-danger" },
  assets_down: { icon: Pulse, label: "Ausgefallen", tone: "text-danger" },
  open_maintenance: { icon: Wrench, label: "Offene Wartungen", tone: "text-warning" },
  open_inspections: { icon: SealCheck, label: "Offene Prüfungen", tone: "text-warning" },
  overdue_inspections: { icon: Clock, label: "Überfällige Prüfungen", tone: "text-danger" },
  first_time_fix: { icon: ChartLineUp, label: "First-Time-Fix", tone: "text-success", suffix: "%" },
  digital_protocol_rate: { icon: ListChecks, label: "Digitale Protokolle", tone: "text-success", suffix: "%" },
  open_due: { icon: BellRinging, label: "Offene Fälligkeiten", tone: "text-warning" },
};

const KpiCard = ({ wk, value }) => {
  const m = META[wk];
  const Icon = m.icon;
  return (
    <Card data-testid={`kpi-${wk}`} className="group flex flex-col justify-between gap-4 rounded-md border p-6 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm">
      <div className="flex items-center justify-between">
        <Label2>{m.label}</Label2>
        <Icon size={22} weight="duotone" className={m.tone} />
      </div>
      <div className="font-heading text-4xl font-black tracking-tighter">
        {value}<span className="ml-0.5 text-lg text-muted-foreground">{m.suffix}</span>
      </div>
    </Card>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [edit, setEdit] = useState(false);
  const [drag, setDrag] = useState(null);

  const loadCfg = useCallback(() => api.get("/dashboard/config").then((r) => setWidgets(r.data.widgets)), []);
  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    loadCfg();
  }, [loadCfg]);

  const toggle = (key) => setWidgets((ws) => ws.map((w) => w.key === key ? { ...w, visible: !w.visible } : w));
  const onDragStart = (i) => setDrag(i);
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (drag === null || drag === i) return;
    setWidgets((ws) => {
      const next = [...ws];
      const [moved] = next.splice(drag, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDrag(i);
  };
  const save = async () => {
    await api.put("/dashboard/config", { widgets });
    toast.success("Dashboard-Layout gespeichert");
    setEdit(false); setDrag(null);
  };
  const reset = async () => {
    const { data: r } = await api.post("/dashboard/config/reset");
    setWidgets(r.widgets);
    toast.success("Auf Rollen-Standard zurückgesetzt");
  };

  if (!data) return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-md" />)}
    </div>
  );

  const k = data.kpis;
  const visibleKpis = widgets.filter((w) => w.visible && w.key !== "recent_activity");
  const showActivity = widgets.find((w) => w.key === "recent_activity")?.visible;

  return (
    <div>
      <PageHeader title="Lagebild" subtitle={`Was ist jetzt wichtig? · Rollenbasiert (${data.role}) · konfigurierbar`}>
        {edit ? (
          <>
            <Button size="sm" variant="ghost" className="gap-2" onClick={reset} data-testid="dash-reset"><ArrowCounterClockwise size={16} /> Standard</Button>
            <Button size="sm" className="gap-2" onClick={save} data-testid="dash-save"><Check size={16} weight="bold" /> Speichern</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" className="gap-2" onClick={() => setEdit(true)} data-testid="dash-customize">
            <Sliders size={16} weight="bold" /> Anpassen
          </Button>
        )}
      </PageHeader>

      {edit && (
        <Card className="mb-6 rounded-md border p-4" data-testid="dash-edit-panel">
          <Label2>Widgets · ziehen zum Sortieren, Schalter zum Ein-/Ausblenden</Label2>
          <div className="mt-3 flex flex-col gap-2">
            {widgets.map((w, i) => (
              <div key={w.key} draggable onDragStart={() => onDragStart(i)} onDragOver={(e) => onDragOver(e, i)}
                data-testid={`dash-widget-row-${w.key}`}
                className="flex cursor-grab items-center gap-3 rounded-md border bg-card px-3 py-2 active:cursor-grabbing">
                <DotsSixVertical size={18} className="text-muted-foreground" />
                <span className="flex-1 text-sm font-medium">{w.key === "recent_activity" ? "Letzte Aktivitäten" : META[w.key]?.label || w.key}</span>
                <Switch checked={w.visible} onCheckedChange={() => toggle(w.key)} data-testid={`dash-toggle-${w.key}`} />
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {visibleKpis.map((w) => <KpiCard key={w.key} wk={w.key} value={k[w.key]} />)}
      </div>

      {showActivity && (
        <div className="mt-8">
          <h2 className="mb-4 font-heading text-xl font-bold tracking-tight">Letzte Aktivitäten</h2>
          <Card className="divide-y divide-border rounded-md border">
            {data.recent_activity.length === 0 && <div className="p-6 text-sm text-muted-foreground">Noch keine Ereignisse.</div>}
            {data.recent_activity.map((a) => (
              <div key={a.id} data-testid="activity-row" className="flex items-center justify-between gap-4 px-6 py-3.5">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="font-mono text-xs text-primary">#{a.seq}</span>
                  <span className="truncate font-mono text-sm">{a.event_type}</span>
                </div>
                <div className="flex shrink-0 items-center gap-4 text-xs text-muted-foreground">
                  <span>{a.actor_name}</span>
                  <span>{new Date(a.timestamp).toLocaleString("de-DE")}</span>
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}
